import { useRef, useState, useCallback } from 'react';
import { median, robustZ } from '../engine/stats';
import { Baselines } from '../types';

interface StylusEntry {
  t: number;
  pressure: number;
  speed: number;
}

interface StylusState {
  isDrawing: boolean;
  stylusPressureZ: number;
  stylusSpeedZ: number;
  lastInputTime: number;
}

/**
 * Stylus/pen sensor: tracks pressure and speed from pointer events.
 * Also draws visible strokes on the canvas.
 */
export function useStylusSensor(baselines: Baselines) {
  const [state, setState] = useState<StylusState>({
    isDrawing: false,
    stylusPressureZ: 0,
    stylusSpeedZ: 0,
    lastInputTime: 0,
  });

  const bufferRef = useRef<StylusEntry[]>([]);
  const drawingRef = useRef(false);
  const lastPtRef = useRef<{ x: number; y: number; t: number } | null>(null);
  const lastInputTimeRef = useRef(0);
  const canvasCtxRef = useRef<CanvasRenderingContext2D | null>(null);

  // Calibration
  const calibPressuresRef = useRef<number[]>([]);
  const calibSpeedsRef = useRef<number[]>([]);
  const isCalibRef = useRef(false);

  const startCalibCollection = useCallback(() => {
    calibPressuresRef.current = [];
    calibSpeedsRef.current = [];
    isCalibRef.current = true;
  }, []);

  const stopCalibCollection = useCallback((): { pressures: number[]; speeds: number[] } => {
    isCalibRef.current = false;
    return { pressures: calibPressuresRef.current, speeds: calibSpeedsRef.current };
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    drawingRef.current = true;
    const x = e.nativeEvent.offsetX;
    const y = e.nativeEvent.offsetY;
    lastPtRef.current = { x, y, t: Date.now() };
    lastInputTimeRef.current = Date.now();

    // Get canvas context for drawing
    const canvas = e.currentTarget as HTMLCanvasElement;
    if (canvas && canvas.getContext) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        canvasCtxRef.current = ctx;
        ctx.beginPath();
        ctx.moveTo(x, y);
      }
    }

    setState(prev => ({ ...prev, isDrawing: true, lastInputTime: Date.now() }));
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!drawingRef.current || !lastPtRef.current) return;

    const now = Date.now();
    lastInputTimeRef.current = now;
    const x = e.nativeEvent.offsetX;
    const y = e.nativeEvent.offsetY;
    const pressure = e.pointerType === 'pen' ? e.pressure : 0.5;
    const dx = x - lastPtRef.current.x;
    const dy = y - lastPtRef.current.y;
    const dt = Math.max(1, now - lastPtRef.current.t);
    const speed = Math.sqrt(dx * dx + dy * dy) / dt;

    // Get canvas context (re-acquire if lost)
    if (!canvasCtxRef.current) {
      const canvas = e.currentTarget as HTMLCanvasElement;
      if (canvas?.getContext) canvasCtxRef.current = canvas.getContext('2d');
    }
    const ctx = canvasCtxRef.current;
    if (ctx) {
      ctx.lineWidth = Math.max(1, pressure * 4);
      ctx.strokeStyle = `rgba(79, 140, 255, ${0.5 + pressure * 0.5})`;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineTo(x, y);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x, y);
    }

    bufferRef.current.push({ t: now, pressure, speed });
    if (bufferRef.current.length > 200) bufferRef.current.shift();

    if (isCalibRef.current) {
      calibPressuresRef.current.push(pressure);
      calibSpeedsRef.current.push(speed);
    }

    lastPtRef.current = { x, y, t: now };

    // Compute Z-scores
    const recent = bufferRef.current.filter(s => now - s.t < 5000);
    let pZ = 0, sZ = 0;
    if (recent.length >= 3 && baselines.stylusPressure && baselines.stylusSpeed) {
      const curP = median(recent.map(s => s.pressure));
      const curS = median(recent.map(s => s.speed));
      pZ = robustZ(curP, baselines.stylusPressure.med, baselines.stylusPressure.mad);
      sZ = robustZ(curS, baselines.stylusSpeed.med, baselines.stylusSpeed.mad);
    }

    setState({ isDrawing: true, stylusPressureZ: pZ, stylusSpeedZ: sZ, lastInputTime: now });
  }, [baselines.stylusPressure, baselines.stylusSpeed]);

  const handlePointerUp = useCallback(() => {
    drawingRef.current = false;
    lastPtRef.current = null;
    canvasCtxRef.current = null;
    setState(prev => ({ ...prev, isDrawing: false }));
  }, []);

  const getBuffer = useCallback(() => bufferRef.current, []);

  return {
    ...state,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    startCalibCollection,
    stopCalibCollection,
    getBuffer,
  };
}
